// ── 콜 목록 섹션 (모바일 카드 + 데스크톱 테이블) ─────────────────────────────

import React from 'react';
import AdminColHeader from '../../../components/AdminColHeader';
import Pagination from '../../../components/ui/Pagination';
import { LIST_COLUMNS } from './constants';
import { StdPageSearch } from '../../admin/adminlayout/designStandard';
import {
  sectionStyle, sheetWrapStyle, sheetRowStyle, headerCell, valueCell,
  singleLineValueCell, empty, miniBtn, miniBtn_disabled, colResizeHandle,
} from './formStyles';
import {
  filterRowStyle, filterKeepBtnStyle, filterSearchInputStyle, filterCountBadgeStyle,
  mobileAdminListStyle, mobileAdminCardStyle, mobileEmployeeCardStyle,
  mobileAdminTopStyle, mobileAdminTitleStyle, mobileAdminSubStyle,
  mobileAdminGridStyle, mobileAdminItemStyle, mobileAdminLabelStyle,
  mobileAdminValueStyle, mobileAdminContentValueStyle, mobileAdminActionsStyle,
  sectionHeaderRowStyle, sectionLabelStyle, listCountBadgeStyle,
} from './listStyles';
import {
  callFeedbackMemoStyle, callKeepChipClickableStyle,
} from './listChipStyles';
// 보조 컴포넌트 (CopyMeetingButton, ContractResultChip) — CallListSubComponents.jsx로 분리
import { CopyMeetingButton, ContractResultChip, FeedbackResultChip } from './CallListSubComponents';

export function CallList({
  adminMode, isMobile, loading, filteredList,
  filterKeep, setFilterKeep, filterQuery, setFilterQuery,
  // 페이지 나누기(2026-10-23, 20줄씩) — 그리는 건 pagedList, 건수는 filteredList 전체
  pagedList, listPage, listTotalPages, setListPage,
  listTemplate, listMinWidth, visibleListColumns,
  openEditModal, openKeepReleaseModal, onResizeStart,
  onFeedbackEdit, // 결과 칩 ✎ — 고치기·지우기 창(2026-11-03, 없으면 예전처럼 글자만)
  // onDelete, deletingId: 삭제는 수정 모달 내부에서만 처리 (목록에서 제거)
}) {
  // 모바일 개별 카드 컴포넌트
  const MobileCard = ({ row }) => {
    const baseStyle = adminMode ? mobileAdminCardStyle : mobileEmployeeCardStyle;
    const cardSt = row.is_contracted
      ? { ...baseStyle,
          background: row.contract_status === 'cancelled' ? 'rgba(217,119,6,0.08)' : 'rgba(22,163,74,0.08)',
          borderLeft: `3px solid ${row.contract_status === 'cancelled' ? '#d97706' : '#16a34a'}` }
      : baseStyle;

    return (
      <div style={cardSt}>
        <div style={mobileAdminTopStyle}>
          <div style={mobileAdminTitleStyle}>{row.company_name || '-'}</div>
          <div style={mobileAdminSubStyle}>
            {adminMode ? `${row.call_date || '-'} · ${row.user_name || '-'}` : (row.call_date || '-')}
          </div>
        </div>
        <div style={mobileAdminGridStyle}>
          <div style={mobileAdminItemStyle}>
            <span style={mobileAdminLabelStyle}>상품</span>
            <span style={mobileAdminValueStyle}>{row.ticket_type || '-'}</span>
          </div>
          <div style={mobileAdminItemStyle}>
            <span style={mobileAdminLabelStyle}>방문일자</span>
            <span style={mobileAdminValueStyle}>{row.visit_date || '-'}</span>
          </div>
          <div style={mobileAdminItemStyle}>
            <span style={mobileAdminLabelStyle}>미팅시간</span>
            <span style={mobileAdminValueStyle}>{row.visit_time || '-'}</span>
          </div>
          {adminMode ? (
            <div style={mobileAdminItemStyle}>
              <span style={mobileAdminLabelStyle}>마케팅담당자</span>
              <span style={mobileAdminValueStyle}>{row.sales_person_name || '-'}</span>
            </div>
          ) : (
            <div style={mobileAdminItemStyle}>
              <span style={mobileAdminLabelStyle}>대표자</span>
              <span style={mobileAdminValueStyle}>{row.ceo_name || '-'}</span>
            </div>
          )}
          <div style={{ ...mobileAdminItemStyle, gridColumn: '1 / -1' }}>
            <span style={mobileAdminLabelStyle}>주소</span>
            <span style={mobileAdminValueStyle}>{row.address || '-'}</span>
          </div>
          <div style={{ ...mobileAdminItemStyle, gridColumn: '1 / -1' }}>
            <span style={mobileAdminLabelStyle}>연락처</span>
            <span style={mobileAdminValueStyle}>{row.phone || '-'} / {row.mobile_phone || '-'}</span>
          </div>
          <div style={{ ...mobileAdminItemStyle, gridColumn: '1 / -1' }}>
            <span style={mobileAdminLabelStyle}>내용</span>
            <span style={mobileAdminContentValueStyle}>{row.content || '-'}</span>
          </div>
        </div>
        {/* 결과 chip */}
        {(row.is_contracted || row.feedback || row.is_keep) && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            <ContractResultChip row={row} />
            {row.is_keep && !row.is_contracted && (
              <button style={callKeepChipClickableStyle} onClick={() => openKeepReleaseModal(row)}>☆ 킵</button>
            )}
            {row.feedback && !row.is_contracted && (
              <FeedbackResultChip row={row} onEdit={onFeedbackEdit} />
            )}
            {row.feedback_memo && !row.is_contracted && (
              <span style={callFeedbackMemoStyle}>{row.feedback_memo}</span>
            )}
          </div>
        )}
        <div style={mobileAdminActionsStyle}>
          {/* 복사 버튼 — 수정 버튼 왼쪽 */}
          <CopyMeetingButton row={row} />
          <button style={row.is_contracted ? miniBtn_disabled : miniBtn}
            disabled={Boolean(row.is_contracted)}
            title={row.is_contracted ? '계약완료된 콜은 계약현황에서 수정하세요' : ''}
            onClick={() => openEditModal(row)}>
            수정
          </button>
          {/* 삭제 버튼은 수정 모달 내부에서만 제공 — 목록에서 제거 */}
        </div>
      </div>
    );
  };

  return (
    <section style={sectionStyle(isMobile)}>
      {/* 직원 전용 목록 헤더 */}
      {!adminMode && (
        <div style={sectionHeaderRowStyle}>
          <span style={sectionLabelStyle}>내역</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button type="button" style={filterKeepBtnStyle(filterKeep)}
              onClick={() => setFilterKeep((v) => !v)}>
              ☆ 킵
            </button>
            {!loading && <span style={listCountBadgeStyle}>{filteredList.length}건</span>}
          </div>
        </div>
      )}
      {/* 직원 검색칸 (2026-10-23 "주소 검색도 추가해줘" — 관리 화면과 같은 전체 검색, 주소 포함) */}
      {!adminMode && (
        <div style={filterRowStyle}>
          <StdPageSearch isMobile={isMobile} mobileStyle={filterSearchInputStyle} type="text" placeholder="검색 (상호·주소·전화·대표자·내용 등)"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)} />
        </div>
      )}
      {/* 관리자 목록 필터 행 */}
      {adminMode && (
        <div style={filterRowStyle}>
          <StdPageSearch isMobile={isMobile} mobileStyle={filterSearchInputStyle} type="text" placeholder="전체 검색 (상호·주소·전화·담당자·마케터·상품 등)"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)} />
          <button type="button" style={filterKeepBtnStyle(filterKeep)}
            onClick={() => setFilterKeep((v) => !v)}>
            ☆ 킵
          </button>
          {(filterKeep || filterQuery) && (
            <span style={filterCountBadgeStyle}>{filteredList.length}건</span>
          )}
        </div>
      )}

      {loading && <div style={empty}>불러오는 중...</div>}
      {!loading && filteredList.length === 0 && <div style={empty}>조건에 맞는 콜이 없습니다.</div>}

      {!loading && filteredList.length > 0 && (
        isMobile ? (
          <div style={mobileAdminListStyle}>
            {pagedList.map((row) => <MobileCard key={row.id} row={row} />)}
          </div>
        ) : (
          <div style={sheetWrapStyle}>
            <div style={{ minWidth: listMinWidth }}>
              {/* 헤더 행 */}
              <div style={{ ...sheetRowStyle, gridTemplateColumns: listTemplate, background: 'var(--text)', fontWeight: 700 }}>
                {visibleListColumns.map((c, index) => (
                  <div key={c.key} style={{ ...headerCell, position: 'relative', paddingRight: 10 }}>
                    {adminMode && c.db ? <AdminColHeader label={c.label} db={c.db} /> : c.label}
                    {index < visibleListColumns.length - 1 && (
                      <div style={colResizeHandle}
                        onMouseDown={(e) => onResizeStart(LIST_COLUMNS.findIndex((x) => x.key === c.key), e)} />
                    )}
                  </div>
                ))}
              </div>

              {/* 데이터 행 */}
              {pagedList.map((row, rowIdx) => {
                // 직원 모드: 맨 위 1행(1페이지 첫 줄)은 여러줄 표시(등록 확인용), 나머지는 1줄 말줄임
                const dataStyle = (!adminMode && listPage === 1 && rowIdx === 0) ? valueCell : singleLineValueCell;
                return (
                  <div key={row.id} style={{
                    ...sheetRowStyle, gridTemplateColumns: listTemplate,
                    ...(row.is_contracted && {
                      background: row.contract_status === 'cancelled' ? 'rgba(217,119,6,0.08)' : 'rgba(22,163,74,0.08)',
                    }),
                  }}>
                    {adminMode && <div style={dataStyle} title={row.user_name || '-'}>{row.user_name || '-'}</div>}
                    <div style={dataStyle} title={row.call_date || '-'}>{row.call_date || '-'}</div>
                    <div style={dataStyle} title={row.ticket_type || '-'}>{row.ticket_type || '-'}</div>
                    <div style={dataStyle} title={row.visit_date || '-'}>{row.visit_date || '-'}</div>
                    <div style={dataStyle} title={row.company_name || '-'}>{row.company_name || '-'}</div>
                    <div style={dataStyle} title={row.industry || '-'}>{row.industry || '-'}</div>
                    <div style={dataStyle} title={row.address || '-'}>{row.address || '-'}</div>
                    <div style={dataStyle} title={row.visit_time || '-'}>{row.visit_time || '-'}</div>
                    <div style={dataStyle} title={row.phone || '-'}>{row.phone || '-'}</div>
                    <div style={dataStyle} title={row.mobile_phone || '-'}>{row.mobile_phone || '-'}</div>
                    <div style={dataStyle} title={row.ceo_name || '-'}>{row.ceo_name || '-'}</div>
                    <div style={dataStyle} title={row.content || '-'}>{row.content || '-'}</div>
                    <div style={dataStyle} title={row.sales_person_name || '-'}>{row.sales_person_name || '-'}</div>
                    <div style={{ ...valueCell, justifyContent: 'center', gap: 4 }}>
                      {/* 복사 버튼 — 수정 버튼 왼쪽 / 삭제는 수정 모달 내부에서만 제공 */}
                      <CopyMeetingButton row={row} />
                      <button style={row.is_contracted ? miniBtn_disabled : miniBtn}
                        disabled={Boolean(row.is_contracted)}
                        title={row.is_contracted ? '계약완료된 콜은 계약현황에서 수정하세요' : ''}
                        onClick={() => openEditModal(row)}>수정</button>
                    </div>
                    {/* 결과 셀 */}
                    <div style={{ ...valueCell, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                      <ContractResultChip row={row} />
                      {row.is_keep && !row.is_contracted && (
                        <button style={callKeepChipClickableStyle} onClick={() => openKeepReleaseModal(row)}>☆ 킵</button>
                      )}
                      {row.feedback && !row.is_contracted && (
                        <FeedbackResultChip row={row} onEdit={onFeedbackEdit} />
                      )}
                      {row.feedback_memo && !row.is_contracted && (
                        <span style={callFeedbackMemoStyle}>{row.feedback_memo}</span>
                      )}
                      {!row.is_contracted && !row.feedback && !row.is_keep && (
                        <span style={{ color: 'var(--border)', fontSize: 11 }}>-</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}
      {/* 페이지 번호 — 계약관리와 같은 공용 부품(한 페이지뿐이면 그리지 않는다) */}
      {!loading && filteredList.length > 0 && (
        <Pagination page={listPage} totalPages={listTotalPages} onMove={setListPage} />
      )}
    </section>
  );
}
